import * as React from 'react';
import { css } from 'styled-components';
import styled from '@independent-software/typeui/styles/Theme'

import { IMenu } from '../../config/Menu';
import { Group } from './SidebarGroup';
import { AuthContext } from '../../AuthContext';
import { useLocation } from 'react-router-dom';

interface IProps {
  /** @ignore */
  className?: string;
  /** Menu to be shown in sidebar. The menu consists of an object
   *  group keys, its values submenus which consist of keys and values.
   */
  menu: IMenu;
  /** Is sidebar forced open, even on a small screen? */
  open?: boolean;
  /** Fired when user closes the sidebar. */
  onClose?: () => void;
}

const SidebarBase = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const wrapperRef = React.useRef(null as HTMLDivElement);
  const location = useLocation();

  const [active, setActive] = React.useState(() => {
    let active = '';
    // Loop through menu groups.
    Object.keys(props.menu).map(key => {
      const item = props.menu[key];

      // See if this menu group contains the active route by comparing
      // the current route match with each entry key.
      const containsActive = Object.keys(item).reduce((acc, key) => {
        return acc || (item[key].path === location.pathname.substr(0, item[key].path.length));
      }, false);

      // If the group contains the active route,
      // make if the initial active group.
      if(containsActive) active = key;
    });
    return active;
  });

  const handleGroupClick = (key: string) => {
    // If clicked group is already open, close it.
    // If it is not, close old group and open new group.
    setActive((prev) => prev === key ? null : key);
  }

  React.useEffect(() => {
    // Listen for document-wide mousedown event when Sidebar mounts.
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Clean up document-wide mousedown event when Sidebar unmounts.
      document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [])
  
  // Handle document-wide mousedown event by closing the Sidebar if it's open.
  const handleClickOutside = (event: MouseEvent) => {
    // Only close sidebar when it's open.
    if(!props.open) return;
    let elem:Element = event.target as Element;
    if (wrapperRef.current && !wrapperRef.current.contains(elem) && props.onClose) {
      props.onClose();
    }
  }

  return (
    <div className={props.className} ref={wrapperRef}>
      <Header>
        <Logo><use xlinkHref={"sprites.svg#aqua-logo-text"}></use></Logo>
      </Header>
      <Menu>
        {Object.keys(props.menu).map((key, index) => {
          let item = props.menu[key];
          return <Group 
            auth={auth}
            key={index} 
            open={active === key} 
            title={key} 
            menu={item} 
            onClick={handleGroupClick}/>
        })}
      </Menu>
    </div>
  );
}

const Logo = styled.svg`
  width: 95%;
  height: 100px;
`

const Header = styled.div`
  box-sizing: border-box;
  position: relative;
  max-width: 250px;
  padding: 10px;
  background-image: rgb(16, 108, 200);
`

const Menu = styled.div`
  padding: 10px 0;
  overflow-y: auto;
`

const SidebarStyled = styled(SidebarBase)`
  /* Sidebar is positioned on left side of screen: */
  position: absolute;
  box-sizing: border-box;
  z-index: 1;
  left: 0;

  /* Top & bottom are slightly off-screen 
   * so that dropshadow corners cannot be seen. */
  top: -10px;
  bottom: -10px;
  padding: 10px 0;;

  /* Width */
  width: 250px;
  max-width: 250px;

  /* On small screens, sidebar is off the screen to the left. 
   * unless it's forced open. */
  transition: left ${p => p.theme.transition.duration}s ease;
  ${p => !p.open && css`
    @media (max-width: ${p => p.theme.smallScreen}px) {
      left: -250px;
    }
  `}  

  /* Colors */
  background: rgb(16, 108, 200);
  color: #fff;
  fill: #fff;

  /* Dropshadow on the right */ 
  border-right: solid 1px darkblue;
  /* box-shadow: 1px 0px 5px 0px rgb(0,0,0,0.75); */
`

// Storybook won't find properties if we don't create a component.
const Sidebar = (props: IProps) => 
  <SidebarStyled {...props}></SidebarStyled>

export { Sidebar }
